import React, { useState, useContext, useEffect, useLayoutEffect, useCallback } from 'react';
import LogoutButton from '../Shared/LogoutButton/LogoutButton';
import { Link } from 'react-router-dom';
import { UserContext } from '../../UserContext';
import { isGlobalAdmin, isClinicLevelAdmin } from '../../utils/permissions';
import GearPopup from './GearPopup';
import IntroGuide from '../IntroGuide/IntroGuide';
import QuickPatientModal from './QuickPatientModal';
import PhraseManager from '../PhraseManager/PhraseManager';
import apiClient from '../../apiClient';
import logo from '../../assets/sidebarlogo.png';
import './Sidebar.css';

// Pin-läget sparas per användare i localStorage (ingen backend).
const pinStorageKey = (user) => `aion_sidebar_pinned:${user?.user_id ?? 'anon'}`;

const readPinned = (user) => {
  try {
    return localStorage.getItem(pinStorageKey(user)) === '1';
  } catch (e) {
    return false;
  }
};

const writePinned = (user, pinned) => {
  try {
    localStorage.setItem(pinStorageKey(user), pinned ? '1' : '0');
  } catch (e) {
    // localStorage otillgängligt (privat läge etc.) — pin gäller då bara sessionen
  }
};

// Undermeny-komponent med hover och fixed positioning.
// `enabled` = sidomenyn är utfälld och klar (dropdownen ligger vid --sidebar-expanded).
const SubMenu = ({ icon, label, enabled = true, children }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [position, setPosition] = useState({ top: 0 });
  const triggerRef = React.useRef(null);

  const handleMouseEnter = () => {
    if (triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      setPosition({ top: rect.top });
    }
    setIsOpen(true);
  };

  return (
    <div
      className="submenu-container"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={() => setIsOpen(false)}
    >
      <button ref={triggerRef} className="submenu-trigger sidebar-link" title={label}>
        <i className={icon}></i>
        <span className="sidebar-label">{label}</span>
        <i className="fas fa-chevron-right submenu-arrow"></i>
      </button>
      {isOpen && enabled && (
        <div
          className="submenu-dropdown"
          style={{ top: position.top }}
        >
          {children}
        </div>
      )}
    </div>
  );
};

function Sidebar() {
  const { user } = useContext(UserContext);
  const [isGearPopupOpen, setIsGearPopupOpen] = useState(false);
  const [isIntroGuideOpen, setIsIntroGuideOpen] = useState(false);
  const [useInternalJournal, setUseInternalJournal] = useState(false);
  const [isQuickPatientOpen, setIsQuickPatientOpen] = useState(false);
  const [isPhraseManagerOpen, setIsPhraseManagerOpen] = useState(false);

  // Autoclose-rail: smal som standard, utfälld vid hover (overlay) eller när fäst.
  const [pinned, setPinned] = useState(() => readPinned(user));
  const [hovered, setHovered] = useState(false);
  const userId = user?.user_id;

  // Byt användare (utan omladdning) → läs den användarens pin-läge
  useEffect(() => {
    setPinned(readPinned(user));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  // Pin styr den RESERVERADE bredden (--sidebar-width) via klass på <html>
  // så att .main-content m.fl. flyttar bara vid pin, aldrig vid hover.
  // useLayoutEffect: sätts före första paint så pinnade användare inte får
  // en 0.3s-glidning av innehållet vid varje omladdning.
  useLayoutEffect(() => {
    document.documentElement.classList.toggle('sidebar-pinned', pinned);
    return () => document.documentElement.classList.remove('sidebar-pinned');
  }, [pinned]);

  const togglePin = () => {
    const next = !pinned;
    setPinned(next);
    writePinned(user, next);
  };

  // Modaler renderas som barn till .sidebar → mus över modalen skulle annars
  // hålla menyn utfälld. Ignorera hover medan en modal är öppen.
  const modalOpen = isGearPopupOpen || isIntroGuideOpen || isQuickPatientOpen || isPhraseManagerOpen;
  useEffect(() => {
    if (!modalOpen) setHovered(false);
  }, [modalOpen]);

  // "hovered" = mus över menyn ELLER tangentbordsfokus inuti den
  const handleMouseActivity = useCallback(() => {
    if (!hovered) setHovered(true);
  }, [hovered]);

  const handleBlur = (e) => {
    if (!e.currentTarget.contains(e.relatedTarget)) setHovered(false);
  };

  const expanded = pinned || (hovered && !modalOpen);

  // Undermenyer får öppnas först när utfällnings-animationen (0.3s) är klar,
  // annars hamnar dropdownen vid x=250 medan menyn fortfarande är smal.
  const [submenusReady, setSubmenusReady] = useState(pinned);
  useEffect(() => {
    if (!expanded) {
      setSubmenusReady(false);
      return undefined;
    }
    const t = setTimeout(() => setSubmenusReady(true), 300);
    return () => clearTimeout(t);
  }, [expanded]);

  // Hämta klinik-inställningar för att visa/dölja journalmodulen
  useEffect(() => {
    const fetchClinicSettings = async () => {
      try {
        const resp = await apiClient.get('/api/journal/clinic/settings');
        setUseInternalJournal(resp.data.use_internal_journal || false);
      } catch (error) {
        // Kliniken har inte modulerna aktiverade eller anrop misslyckades
        setUseInternalJournal(false);
      }
    };

    fetchClinicSettings();
  }, []);

  const handleLogout = () => {
    localStorage.removeItem('access_token');
    window.location.href = '/login';
  };

  const toggleGearPopup = () => {
    setIsGearPopupOpen(!isGearPopupOpen);
  };

  const toggleIntroGuide = () => {
    setIsIntroGuideOpen(!isIntroGuideOpen);
  };

  const sidebarClass = ['sidebar', expanded ? 'expanded' : 'rail', pinned ? 'pinned' : '']
    .filter(Boolean)
    .join(' ');

  return (
    <div
      className={sidebarClass}
      onMouseEnter={handleMouseActivity}
      onMouseMove={handleMouseActivity}
      onMouseLeave={() => setHovered(false)}
      onFocus={handleMouseActivity}
      onBlur={handleBlur}
    >
      <div className="sidebar-content">
        {/* Logo, pin och användarinfo */}
        <div className="sidebar-header">
          <div className="logo-container">
            <img src={logo} alt="Aion Journal" className="sidebar-logo" />
            <span className="sidebar-wordmark">Aion Journal</span>
            <button
              type="button"
              className="pin-button"
              onClick={togglePin}
              aria-pressed={pinned}
              aria-label={pinned ? 'Lossa menyn' : 'Fäst menyn utfälld'}
              title={pinned ? 'Lossa menyn (fälls ihop automatiskt)' : 'Fäst menyn utfälld'}
            >
              <i className="fas fa-thumbtack" aria-hidden="true"></i>
            </button>
          </div>
          <p className="greeting">Inloggad som: {user?.first_name} {user?.last_name}</p>
        </div>

        {/* Huvudnavigation */}
        <nav className="main-navigation">
          <div className="nav-section">
            <Link to="/" className="sidebar-link" title="Startsida">
              <i className="fas fa-home"></i>
              <span className="sidebar-label">Startsida</span>
            </Link>
          </div>

          {/* Kalender - visas endast om kliniken har inbyggd journalmodul aktiverad */}
          {useInternalJournal && (
            <div className="nav-section">
              <span className="nav-section-label">Kalender</span>
              <Link to="/calendar" className="sidebar-link" title="Bokningar">
                <i className="fas fa-calendar-alt"></i>
                <span className="sidebar-label">Bokningar</span>
              </Link>
              <div className="sidebar-link-with-action">
                <Link to="/patients" className="sidebar-link" title="Patienter">
                  <i className="fas fa-users"></i>
                  <span className="sidebar-label">Patienter</span>
                </Link>
                <button
                  className="sidebar-quick-add"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setIsQuickPatientOpen(true);
                  }}
                  title="Lägg till patient"
                >
                  <i className="fas fa-plus"></i>
                </button>
              </div>
              <Link to="/groups" className="sidebar-link" title="Grupper">
                <i className="fas fa-user-friends"></i>
                <span className="sidebar-label">Grupper</span>
              </Link>
            </div>
          )}

          {/* Admin-sektion med undermenyer (global admin + klinikadmin) */}
          {isClinicLevelAdmin(user) && (
            <div className="nav-section admin-section">
              <span className="nav-section-label">Administration</span>

              {/* Mallar */}
              <SubMenu icon="fas fa-file-alt" label="Mallar" enabled={submenusReady}>
                <Link to="/journalmallar" className="submenu-link">
                  <i className="fas fa-notes-medical"></i> Journalmallar
                </Link>
                <Link to="/kallelsemallar" className="submenu-link">
                  <i className="fas fa-envelope-open-text"></i> Kallelsemallar
                </Link>
                <Link to="/sms-mallar" className="submenu-link">
                  <i className="fas fa-sms"></i> SMS-mallar
                </Link>
                <Link to="/formularmallar" className="submenu-link">
                  <i className="fas fa-clipboard-list"></i> Formulärmallar
                </Link>
                <Link to="/dokumentmallar" className="submenu-link">
                  <i className="fas fa-file-signature"></i> Dokumentmallar
                </Link>
              </SubMenu>

              {/* Kalenderinställningar */}
              <SubMenu icon="fas fa-calendar-check" label="Kalenderinställningar" enabled={submenusReady}>
                <Link to="/calendar-settings" className="submenu-link">
                  <i className="fas fa-cog"></i> Inställningar
                </Link>
                <Link to="/appointment-types" className="submenu-link">
                  <i className="fas fa-clock"></i> Bokningstyper
                </Link>
                <Link to="/schedule-templates" className="submenu-link">
                  <i className="fas fa-calendar-week"></i> Schemamallar
                </Link>
                <Link to="/schedule-assignments" className="submenu-link">
                  <i className="fas fa-user-clock"></i> Schematilldelning
                </Link>
                <Link to="/products" className="submenu-link">
                  <i className="fas fa-box"></i> Produkter
                </Link>
                <Link to="/daily-cash-report" className="submenu-link">
                  <i className="fas fa-cash-register"></i> Dagskassa
                </Link>
                <Link to="/product-statistics" className="submenu-link">
                  <i className="fas fa-chart-bar"></i> Produktstatistik
                </Link>
              </SubMenu>

              {/* Användare & Kliniker */}
              <SubMenu icon="fas fa-users-cog" label="Organisation" enabled={submenusReady}>
                <Link to="/users" className="submenu-link">
                  <i className="fas fa-users"></i> Användare
                </Link>
                {isGlobalAdmin(user) && (
                  <Link to="/clinics" className="submenu-link">
                    <i className="fas fa-hospital"></i> Kliniker
                  </Link>
                )}
              </SubMenu>

              {/* Admin (Spår K2.4) */}
              <SubMenu icon="fas fa-user-shield" label="Admin" enabled={submenusReady}>
                <Link to="/admin/franvaro" className="submenu-link">
                  <i className="fas fa-users-slash"></i> Frånvaro
                </Link>
              </SubMenu>

            </div>
          )}
        </nav>
      </div>

      {/* Guide-knapp */}
      <div className="guide-section">
        <button onClick={toggleIntroGuide} className="sidebar-link guide-link" title="Guide">
          <i className="fas fa-info-circle"></i>
          <span className="sidebar-label">Guide</span>
        </button>
      </div>

      {/* Footer med inställningar och logout */}
      <div className="sidebar-footer">
        <div className="footer-buttons">
          <button
            className="gear-button"
            onClick={toggleGearPopup}
            aria-label="Öppna inställningar"
          >
            <i className="fas fa-cog" aria-hidden="true"></i>
          </button>
          <LogoutButton onLogout={handleLogout} />
        </div>
      </div>

      {isGearPopupOpen && (
        <GearPopup
          onClose={toggleGearPopup}
          onOpenPhraseManager={() => {
            setIsGearPopupOpen(false);
            setIsPhraseManagerOpen(true);
          }}
        />
      )}
      {isIntroGuideOpen && <IntroGuide isOpen={isIntroGuideOpen} onClose={() => setIsIntroGuideOpen(false)} />}
      <QuickPatientModal isOpen={isQuickPatientOpen} onClose={() => setIsQuickPatientOpen(false)} />
      {isPhraseManagerOpen && <PhraseManager onClose={() => setIsPhraseManagerOpen(false)} />}
    </div>
  );
}

export default Sidebar;
